/// <amd-module name="Repos/PullRequestList/Components/PullRequestTable" />
import * as React from "react";
import { PullRequestStatus } from "Repos/Common/Contracts/Git";
import * as Resources from "Repos/PullRequestList/Resources";
import { ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { IScreenContext, ScreenContext, ScreenSize } from "VSS/Core/Util/Screen";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { Ago } from "VSSUI/Ago";
import { Icon } from "VSSUI/Icon";
import { IAnchorProps } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { IPersonaProvider, Persona, PersonaSize } from "VSSUI/Persona";
import { Pill, PillSize, PillVariant } from "VSSUI/Pill";
import { PillGroup, PillGroupOverflow } from "VSSUI/PillGroup";
import {
    IMeasurementStyle,
    ITableBreakpoint,
    ITableColumn,
    ITableRow,
    ITableRowDetails,
    SimpleTableCell,
    Table,
    TableLoadingRow,
    TableRow,
    TwoLineTableCell
} from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { IItemProvider } from "VSSUI/Utilities/Provider";
import { IPullRequest, IPullRequestReviewer, PullRequestVote } from "../Contracts";
import { PullRequestLabels } from "./PullRequestLabels";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { usePageContext } from "VSS/Platform/Layout";
import { IVssPageContext } from "VSS/Platform/Context";

export interface IPullRequestTableProps {
    columns: ITableColumn<IPullRequest>[];
    loadMore?: () => void;
    onActivate?: (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<IPullRequest>) => void;
    pullRequests: IItemProvider<IPullRequest | ObservableValue<IPullRequest | undefined>>;
    tableBreakpoints?: ITableBreakpoint[];
    viewMoreLinkProps?: IAnchorProps;
}

export const PullRequestTable = (props: IPullRequestTableProps) => {
    const DEBOUNCE_TIMEOUT = 5000;
    const loadMoreDebounce = debounce(() => {
        props.loadMore && props.loadMore();
    }, DEBOUNCE_TIMEOUT);
    return (
        <Table
            ariaLabel={Resources.PullRequestTable}
            className="repos-pr-list"
            columns={props.columns}
            itemProvider={props.pullRequests}
            onActivate={props.onActivate}
            selectableText={true}
            showHeader={false}
            tableBreakpoints={props.tableBreakpoints}
            renderLoadingRow={(rowIndex: number, rowDetails: ITableRowDetails<IPullRequest>) => {
                if (props.viewMoreLinkProps) {
                    return (
                        <TableRow index={rowIndex} details={rowDetails} linkProps={props.viewMoreLinkProps}>
                            {props.columns.map((c, columnIndex) => (
                                <SimpleTableCell columnIndex={columnIndex} key={columnIndex}>
                                    {columnIndex === 1 ? Resources.ViewMore : null}
                                </SimpleTableCell>
                            ))}
                        </TableRow>
                    );
                } else {
                    loadMoreDebounce();
                    return <TableLoadingRow columns={props.columns} details={rowDetails} key={rowIndex} rowIndex={rowIndex} />;
                }
            }}
            renderRow={(rowIndex, item, rowDetails) => (
                <Observer key={rowIndex} unvisited={item.unvisited} updates={item.updates}>
                    {(updateProps: { unvisited: boolean; updates: string }) => {
                        if (!rowDetails.ariaSetSize) {
                            rowDetails.ariaSetSize = props.pullRequests.length;
                        }
                        return (
                            <TableRow
                                className={css((updateProps.unvisited || updateProps.updates) && "bolt-list-row-marked")}
                                details={rowDetails}
                                index={rowIndex}
                                linkProps={(item as any).linkProps}
                            >
                                {props.columns.map((tableColumn, columnIndex) => {
                                    if (ObservableLike.getValue(tableColumn.width) !== 0) {
                                        return tableColumn.renderCell(
                                            rowIndex,
                                            columnIndex,
                                            tableColumn,
                                            item,
                                            rowIndex + (rowDetails.ariaRowOffset ? rowDetails.ariaRowOffset : 1)
                                        );
                                    }

                                    return null;
                                })}
                            </TableRow>
                        );
                    }}
                </Observer>
            )}
        />
    );
};

function debounce(
    callback: Function,
    timeout: number | undefined): Function { 
    let timer: ReturnType<typeof setTimeout> | undefined;
    return function (this: any, ...args: any[]) {
        if (!timer) {
            callback.apply(this, args);
        }
        clearTimeout(timer);
        timer = setTimeout(() => {
            timer = undefined;
        }, timeout);
    }
}

export const creatorColumn: ITableColumn<IPullRequest> = {
    id: "creator",
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IPullRequest>, tableItem: IPullRequest) => {
        return (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                <Persona identity={tableItem.createdBy} size={PersonaSize.size32} personaProvider={tableItem.personaProvider} />
            </SimpleTableCell>
        );
    },
    width: new ObservableValue(3),
    widthStyle: IMeasurementStyle.REM
};

export const titleColumn: ITableColumn<IPullRequest> = {
    id: "title",
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IPullRequest>, tableItem: IPullRequest) => {
        return <TitleCell columnIndex={columnIndex} key={columnIndex} pr={tableItem} showRepo={true} tableColumn={tableColumn} />;
    },
    width: new ObservableValue(-100)
};

export const titleNoRepoColumn: ITableColumn<IPullRequest> = {
    id: "title",
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IPullRequest>, tableItem: IPullRequest) => {
        return <TitleCell columnIndex={columnIndex} key={columnIndex} pr={tableItem} showRepo={false} tableColumn={tableColumn} />;
    },
    width: new ObservableValue(-100)
};

export const reviewersColumn: ITableColumn<IPullRequest> = {
    id: "reviewers",
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IPullRequest>, tableItem: IPullRequest) => {
        return (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                <ScreenContext.Consumer>
                    {(screenContext: IScreenContext) => (
                        <Observer screenSize={screenContext.size}>
                            {(props: { screenSize: ScreenSize }) => {
                                const reviewers = tableItem.reviewers.filter(r => !r.isGroup && !r.hasDeclined);
                                if (reviewers.length === 0) {
                                    return <div hidden />;
                                }

                                // Get the reviewers who are individuals (not grouped), picking out
                                // the reviewers who have voted first, followed by non-voters
                                let visibleReviewers = [
                                    ...reviewers.filter(r => r.vote !== PullRequestVote.awaitingResponse),
                                    ...reviewers.filter(r => r.vote === PullRequestVote.awaitingResponse)
                                ];
                                let overflowCount = 0;

                                const maxPersonas = props.screenSize > ScreenSize.xsmall ? 4 : 3;
                                if (visibleReviewers.length > maxPersonas) {
                                    overflowCount = visibleReviewers.length - maxPersonas + 1; // +1 because of overflow persona taking up one
                                    visibleReviewers = visibleReviewers.slice(0, maxPersonas - 1);
                                }

                                return (
                                    <PersonaGroup overflowCount={overflowCount}>
                                        {visibleReviewers.map(r => (
                                            <PullRequestReviewer
                                                key={r.identity.id}
                                                personaProvider={tableItem.personaProvider}
                                                reviewer={r}
                                                showTooltip={true}
                                            />
                                        ))}
                                    </PersonaGroup>
                                );
                            }}
                        </Observer>
                    )}
                </ScreenContext.Consumer>
            </SimpleTableCell>
        );
    },
    width: new ObservableValue(10), // 12px (padding-right) + 24px (persona size) * 3 (max personas shown) + 40 (max for Pill) + 8px (spacing) * 3 (max spacings) + 12px (padding-left) = 160px
    widthStyle: IMeasurementStyle.REM
};

export const commentsColumn: ITableColumn<IPullRequest> = {
    id: "comments",
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IPullRequest>, tableItem: IPullRequest) => {
        return (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                <Observer commentsCount={tableItem.commentsCount}>
                    {(props: { commentsCount: number }) => (
                        <div className={css("flex-row flex-center", props.commentsCount === 0 && "secondary-text")}>
                            <Icon iconName="ActivityFeed" />
                            <span className="repos-pr-list-comment-count">{props.commentsCount}</span>
                        </div>
                    )}
                </Observer>
            </SimpleTableCell>
        );
    },
    width: new ObservableValue(4),
    widthStyle: IMeasurementStyle.REM
};

export const updatesColumn: ITableColumn<IPullRequest> = {
    id: "updates",
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IPullRequest>, tableItem: IPullRequest) => {
        return (
            <Observer key={columnIndex} unvisited={tableItem.unvisited} updates={tableItem.updates}>
                {(props: { unvisited: boolean; updates: string }) => {
                    let dateline;

                    if (
                        (tableItem.status === PullRequestStatus.Completed || tableItem.status === PullRequestStatus.Abandoned) &&
                        tableItem.closedDate
                    ) {
                        const formatString = tableItem.status === PullRequestStatus.Completed ? Resources.Completed : Resources.Abandoned;
                        dateline = (
                            <FormatComponent elementType="div" format={formatString}>
                                <Ago date={tableItem.closedDate} />
                            </FormatComponent>
                        );
                    } else {
                        dateline = (
                            <Observer date={tableItem.date}>
                                {(dateProps: { date: Date }) => {
                                    const formatString = getFormatString(tableItem.status, dateProps.date, tableItem.createdDate);
                                    return (
                                        <FormatComponent elementType="div" format={formatString}>
                                            <Ago date={dateProps.date} />
                                        </FormatComponent>
                                    );
                                }}
                            </Observer>
                        );
                    }
                    if (!props.updates) {
                        return (
                            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn}>
                                {dateline}
                            </SimpleTableCell>
                        );
                    }
                    const updatesLine = (
                        <Tooltip overflowOnly={true}>
                            <div className={css("text-ellipsis", (props.unvisited || props.updates) && "font-weight-semibold")}>{props.updates}</div>
                        </Tooltip>
                    );

                    return (
                        <TwoLineTableCell
                            className="repos-pr-list-updates"
                            columnIndex={columnIndex}
                            tableColumn={tableColumn}
                            line1={updatesLine}
                            line2={dateline}
                        />
                    );
                }}
            </Observer>
        );
    },
    width: new ObservableValue(250)
};

function getFormatString(status: PullRequestStatus, updatedDate: Date, createdDate: Date) {
    let formatString: string =
        (status === PullRequestStatus.Completed && Resources.Completed) ||
        (status === PullRequestStatus.Abandoned && Resources.Abandoned) ||
        Resources.Created;

    if (status !== PullRequestStatus.Completed && status !== PullRequestStatus.Abandoned && updatedDate !== createdDate) {
        formatString = Resources.Updated;
    }
    return formatString;
}

interface ITitleCellProps {
    columnIndex: number;
    pr: IPullRequest;
    showRepo: boolean;
    tableColumn: ITableColumn<IPullRequest>;
}

const TitleCell = (props: ITitleCellProps) => {
    const { columnIndex, pr, showRepo, tableColumn } = props;
    const pageContext: IVssPageContext = usePageContext();

    const improvedLabelsEnabled: boolean = isFeatureFlagEnabled(pageContext, "WebAccess.VersionControl.PullRequestList.ImprovedLabels");
    const line1 = (
        <Observer unvisited={pr.unvisited} updates={pr.updates}>
            {(props: { unvisited: boolean; updates: string }) => {
                return (
                    <>
                        <Tooltip overflowOnly={true}>
                            <div
                                // buton role is needed for correct work of JAWS in controls and tables smart navigation mode
                                role="button"
                                className={css("body-l flex-self-center text-ellipsis", (props.unvisited || props.updates) && "font-weight-semibold")}
                            >
                                {pr.title}
                            </div>
                        </Tooltip>
                        {pr.currentReviewer && pr.currentReviewer.isFlagged && pr.status === PullRequestStatus.Active && (
                            <Icon className="repos-pr-list-flag-icon margin-horizontal-8" iconName="Flag" />
                        )}
                        {pr.draft && (
                            <Pill
                                className="repos-pr-list-draft-pill flex-noshrink margin-left-4"
                                size={PillSize.compact}
                                variant={PillVariant.outlined}
                            >
                                {Resources.Draft}
                            </Pill>
                        )}
                        {pr.bypassedPolicies && (
                            <Pill
                                className="repos-pr-list-conflicts-pill flex-noshrink margin-left-4"
                                size={PillSize.compact}
                                variant={PillVariant.outlined}
                            >
                                <Tooltip text={`${Resources.BypassedTooltip}: ${pr.bypassedPoliciesReason}`}>
                                    <span>{Resources.Bypassed}</span>
                                </Tooltip>
                            </Pill>
                        )}
                        {pr.conflicts && (
                            <Pill
                                className="repos-pr-list-conflicts-pill flex-noshrink margin-left-4"
                                size={PillSize.compact}
                                variant={PillVariant.outlined}
                            >
                                {Resources.Conflicts}
                            </Pill>
                        )}
                        {pr.autoCompleteSetBy && pr.status === PullRequestStatus.Active && (
                            <Pill
                                className="repos-pr-list-auto-complete-pill flex-noshrink margin-left-4"
                                size={PillSize.compact}
                                variant={PillVariant.outlined}
                            >
                                {Resources.AutoComplete}
                            </Pill>
                        )}
                        {pr.currentReviewer && pr.currentReviewer.required && pr.status === PullRequestStatus.Active && (
                            <Pill
                                className={css(
                                    "repos-pr-list-required-pill flex-noshrink margin-left-4",
                                    pr.currentReviewer.vote === PullRequestVote.awaitingResponse && "no-vote"
                                )}
                                size={PillSize.compact}
                                variant={PillVariant.outlined}
                            >
                                <Tooltip text={Resources.RequiredTooltip}>
                                    <span>{Resources.Required}</span>
                                </Tooltip>
                            </Pill>
                        )}
                        {pr.currentReviewer && pr.currentReviewer.hasDeclined && (
                            <Pill
                                className={css("repos-pr-list-declined-pill flex-noshrink margin-left-4")}
                                size={PillSize.compact}
                                variant={PillVariant.outlined}
                            >
                                <Tooltip text={Resources.DeclinedTooltip}>
                                    <span>{Resources.Declined}</span>
                                </Tooltip>
                            </Pill>
                        )}
                        {pr.labels && !improvedLabelsEnabled && (
                            <PillGroup className="margin-left-8" overflow={PillGroupOverflow.fade}>
                                {pr.labels.map(label => (
                                    <Pill key={label.id} size={PillSize.compact}>
                                        {label.name}
                                    </Pill>
                                ))}
                            </PillGroup>
                        )}
                        {pr.labels && improvedLabelsEnabled && <PullRequestLabels labels={pr.labels} />}
                    </>
                );
            }}
        </Observer>
    );
    const line2 = (
        <div className="secondary-text body-s text-ellipsis">
            <FormatComponent format={Resources.RequestInto}>
                {pr.createdBy.displayName}
                {pr.id}
                <>
                    {showRepo && <Icon className="repos-pr-list-git-logo" iconName="GitLogo" />}
                    {showRepo && (
                        <span key="repo" className="padding-horizontal-4">
                            {pr.repository.name}
                        </span>
                    )}
                    <Icon iconName="OpenSource" />
                    <span key="target-ref" className="monospaced-xs padding-horizontal-4">
                        {pr.targetRef.name}
                    </span>
                </>
            </FormatComponent>
        </div>
    );
    return <TwoLineTableCell columnIndex={columnIndex} line1={line1} line2={line2} tableColumn={tableColumn} />;
};

interface IPersonaGroupProps {
    overflowCount: number;
}

const PersonaGroup = (props: IPersonaGroupProps & Readonly<{ children?: React.ReactNode }>) => {
    let overflowPill = null;
    if (props.overflowCount) {
        overflowPill = (
            <Pill size={PillSize.compact} variant={PillVariant.standard}>
                {"+" + props.overflowCount}
            </Pill>
        );
    }
    const spacingClassName = "rhythm-horizontal-8";
    return (
        <div className={css("flex-row flex-center", spacingClassName)}>
            {props.children}
            {overflowPill}
        </div>
    );
};

interface IPullRequestReviewerProps {
    personaProvider?: IPersonaProvider;
    reviewer: IPullRequestReviewer;
    showTooltip: boolean;
}

const PullRequestReviewer = (props: IPullRequestReviewerProps) => {
    const ariaLabel = `${props.reviewer.identity.displayName} ${getVoteText(props.reviewer.vote)}`;
    return (
        <div className="pr-reviewer relative">
            <Persona
                ariaLabel={ariaLabel}
                identity={props.reviewer.identity}
                personaProvider={props.personaProvider}
                size={PersonaSize.size24}
                tooltipProps={props.showTooltip ? { renderContent: () => <PullRequestReviewerTooltipContent reviewer={props.reviewer} /> } : null}
            />
            <PullRequestReviewerVoteIcon isInTooltip={!props.showTooltip} vote={props.reviewer.vote} />
        </div>
    );
};

interface IPullRequestReviewerTooltipContentProps {
    reviewer: IPullRequestReviewer;
}

const PullRequestReviewerTooltipContent = (props: IPullRequestReviewerTooltipContentProps) => {
    return (
        <div className="flex-row flex-center">
            <PullRequestReviewer reviewer={props.reviewer} showTooltip={false} />
            <div className="flex-column margin-left-8">
                <span>{props.reviewer.identity.displayName}</span>
                <span>{getVoteText(props.reviewer.vote)}</span>
            </div>
        </div>
    );
};

interface IPullRequestReviewerVoteIconProps {
    isInTooltip: boolean;
    vote: PullRequestVote;
}

function getVoteText(vote: PullRequestVote) {
    switch (vote) {
        case PullRequestVote.approved:
            return Resources.Approved;
        case PullRequestVote.approvedWithComment:
            return Resources.ApprovedWithSuggestions;
        case PullRequestVote.notReady:
            return Resources.WaitingForAuthor;
        case PullRequestVote.rejected:
            return Resources.Rejected;
        default:
            return "";
    }
}

const PullRequestReviewerVoteIcon = (props: IPullRequestReviewerVoteIconProps) => {
    let iconName,
        voteClassName,
        ariaLabel = "";
    switch (props.vote) {
        case PullRequestVote.approved:
        case PullRequestVote.approvedWithComment:
            iconName = "CompletedSolid";
            voteClassName = "approved";
            ariaLabel = "";
            break;
        case PullRequestVote.notReady:
            iconName = "AwayStatus";
            voteClassName = "waiting";
            ariaLabel = "";
            break;
        case PullRequestVote.rejected:
            iconName = "StatusErrorFull";
            voteClassName = "rejected";
            ariaLabel = "";
    }
    if (iconName) {
        return (
            <Icon
                iconName={iconName}
                className={css("repos-pr-reviewer-vote absolute", voteClassName, props.isInTooltip && "tooltip")}
                ariaLabel={ariaLabel}
            />
        );
    }
    return null;
};